/* eslint-disable react-refresh/only-export-components */
import { AppIcon } from '@/components/ui/icons';
import { formatInterfaceText } from '@/lib/ui/interfaceText';
import React, { useState, useCallback, ReactNode, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ToastContext, Toast, ToastType, DEFAULT_DURATIONS } from '../contexts/ToastContext';

// Custom hook to use toast context
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Hide a specific toast by ID
  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Show a toast notification
  const showToast = useCallback((options: Omit<Toast, 'id'> | { type: ToastType; message?: string; title?: string; duration?: number }) => {
    const id = Date.now().toString();
     
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const type = (options as any).type as ToastType;
     
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hasAction = !!(options as any).action;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const duration = (options as any).duration || (hasAction ? 8000 : DEFAULT_DURATIONS[type]);

     
    const toast: Toast = {
       
      id,
      type,
       
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      title: (options as any).title ?? (type === 'success' ? 'Success' : type === 'error' ? 'Error' : type === 'warning' ? 'Warning' : 'Info'),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      message: (options as any).message,
      duration,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      action: (options as any).action,
    };

    setToasts(prev => [...prev, toast]);

    // Auto-hide toast after duration
    if (duration !== Infinity) {
      setTimeout(() => {
        hideToast(id);
      }, duration);
    }

    return id;
  }, [hideToast]);

  // Hide all toast notifications
  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const success = useCallback((message: string, title = 'Success', duration?: number) =>
    showToast({ type: 'success', title, message, duration }), [showToast]);
  const error = useCallback((message: string, title = 'Error', duration?: number) =>
    showToast({ type: 'error', title, message, duration }), [showToast]);
  const info = useCallback((message: string, title = 'Info', duration?: number) =>
    showToast({ type: 'info', title, message, duration }), [showToast]);
  const warning = useCallback((message: string, title = 'Warning', duration?: number) =>
    showToast({ type: 'warning', title, message, duration }), [showToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, hideToast, hideAllToasts, success, error, info, warning }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};



// Icon component based on toast type
const ToastIcon: React.FC<{ type: ToastType }> = ({ type }) => {
  switch (type) {
    case 'success':
      return <AppIcon name="checkCircle" className="w-5 h-5 text-status-success-foreground" />;
    case 'error':
      return <AppIcon name="alertCircle" className="w-5 h-5 text-status-danger-foreground" />;
    case 'info':
      return <AppIcon name="info" className="w-5 h-5 text-status-info-foreground" />;
    case 'warning':
      return <AppIcon name="alertTriangle" className="w-5 h-5 text-status-warning-foreground" />;
  }
};

// Toast container that renders all active toasts
const ToastContainer: React.FC = () => {
  const context = React.useContext(ToastContext);
  if (!context) return null;

  const { toasts, hideToast } = context;

  const getToastStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'ax-side-accent--success';
      case 'error':
        return 'ax-side-accent--critical';
      case 'info':
        return 'ax-side-accent--info';
      case 'warning':
        return 'ax-side-accent--warning';
    }
  };

  return (
    <div
      className="fixed top-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      <AnimatePresence>
        {toasts.map((toast: Toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className={`
              ax-overlay-panel ax-side-accent pointer-events-auto overflow-hidden
              rounded-[var(--ax-radius-overlay)] card-padding-sm flex gap-3 items-start
              ${getToastStyles(toast.type)}
            `}
          >
            <div className="flex-shrink-0">
              <ToastIcon type={toast.type} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-body-sm">{formatInterfaceText(toast.title ?? '')}</h4>
                {toast.action && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toast.action!.onClick();
                      hideToast(toast.id);
                    }}
                    className="text-xs font-medium text-text-secondary hover:text-text-primary underline underline-offset-2 decoration-zinc-400/50 hover:decoration-zinc-600 transition-colors whitespace-nowrap"
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
              {toast.message && <p className="text-xs mt-1 text-muted">{formatInterfaceText(toast.message)}</p>}
            </div>
            <button
              onClick={() => hideToast(toast.id)}
              className="ax-icon-button flex-shrink-0"
              aria-label="Close toast"
            >
              <AppIcon name="x" className="w-4 h-4 text-muted" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
